import {useCallback, useEffect, useRef} from 'react';

import {observeRegionChanges} from './observeRegionChanges';
import type {TextProjection} from './annotationTextProjection';
import {
  buildTextProjection,
  FILTERED_ATTRIBUTES,
  isInsideExcludedSubtree
} from './annotationTextProjection';

interface TextProjectionHandlers {
  onMutate?: () => void;
  onSettle?: () => void;
}

/**
 * A lazily built, cached text projection for `container`, invalidated by one
 * observer for the container's lifetime. Handlers are read through a ref, so
 * callers pass plain closures — the latest one always fires, none go stale.
 * `onMutate` fires before the settle: painted Highlight Ranges are live and slide
 * while MathJax rewrites the DOM, so callers clear at once and repaint on settle.
 */
export function useTextProjection(
  container: HTMLElement | null,
  handlers: TextProjectionHandlers = {}
): {
  getProjection: () => TextProjection | null;
  /** Drops the cache after a DOM change the observer is told to skip (the region's own marks). */
  invalidateProjection: () => void;
} {
  const projectionRef = useRef<TextProjection | null>(null);
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!container) {
      return undefined;
    }
    const stopObserving = observeRegionChanges(
      container,
      {
        onMutate: () => {
          projectionRef.current = null;
          handlersRef.current.onMutate?.();
        },
        onSettle: () => handlersRef.current.onSettle?.()
      },
      {
        attributeFilter: FILTERED_ATTRIBUTES,
        ignoreRecord: (record) =>
          record.type !== 'attributes' && isInsideExcludedSubtree(record.target, container)
      }
    );
    return () => {
      stopObserving();
      projectionRef.current = null;
    };
  }, [container]);

  const getProjection = useCallback(() => {
    if (!container) {
      return null;
    }
    projectionRef.current ??= buildTextProjection(container);
    return projectionRef.current;
  }, [container]);

  const invalidateProjection = useCallback(() => {
    projectionRef.current = null;
  }, []);

  return {getProjection, invalidateProjection};
}
